import type { AppAction } from '@app/state'

import type { IApiIntegration } from './interfaces'

export const enum ActionStatus {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export interface State {
  integrations: IApiIntegration[]
  integrationListStatus?: ActionStatus
  integrationUpdateStatus?: ActionStatus
}

export enum ActionType {
  ClearIntegrationContext = 'CLINIC/INTEGRATION/CONTEXT/CLEAR',
  CreateIntegration = 'CLINIC/INTEGRATION/CREATE',
  CreateIntegrationBegin = 'CLINIC/INTEGRATION/CREATE/BEGIN',
  CreateIntegrationSuccess = 'CLINIC/INTEGRATION/CREATE/SUCCESS',
  CreateIntegrationFailure = 'CLINIC/INTEGRATION/CREATE/FAILURE',
  ListIntegrations = 'CLINIC/INTEGRATIONS/LIST',
  ListIntegrationsBegin = 'CLINIC/INTEGRATIONS/LIST/BEGIN',
  ListIntegrationsSuccess = 'CLINIC/INTEGRATIONS/LIST/SUCCESS',
  ListIntegrationsFailure = 'CLINIC/INTEGRATIONS/LIST/FAILURE',
}

export type Action =
  | { type: ActionType.ClearIntegrationContext }
  | { type: ActionType.CreateIntegration; i: IApiIntegration }
  | { type: ActionType.CreateIntegrationBegin }
  | { type: ActionType.CreateIntegrationSuccess; id: string }
  | { type: ActionType.CreateIntegrationFailure; error: string }
  | { type: ActionType.ListIntegrations }
  | { type: ActionType.ListIntegrationsBegin }
  | {
      type: ActionType.ListIntegrationsSuccess
      integrations: IApiIntegration[]
    }
  | { type: ActionType.ListIntegrationsFailure; error: string }

export const actions = {
  clearIntegrationContext(): Action {
    return { type: ActionType.ClearIntegrationContext }
  },
  createIntegration(i: IApiIntegration): Action {
    return { type: ActionType.CreateIntegration, i }
  },
  createIntegrationBegin(): Action {
    return { type: ActionType.CreateIntegrationBegin }
  },
  createIntegrationSuccess(id: string): Action {
    return { type: ActionType.CreateIntegrationSuccess, id }
  },
  createIntegrationFailure(error: string): Action {
    return { type: ActionType.CreateIntegrationFailure, error }
  },
  listIntegrations(): Action {
    return { type: ActionType.ListIntegrations }
  },
  listIntegrationsBegin(): Action {
    return { type: ActionType.ListIntegrationsBegin }
  },
  listIntegrationsSuccess(integrations: IApiIntegration[]): Action {
    return { type: ActionType.ListIntegrationsSuccess, integrations }
  },
  listIntegrationsFailure(error: string): Action {
    return { type: ActionType.ListIntegrationsFailure, error }
  },
}

export const initialState: State = {
  integrations: [],
  integrationListStatus: undefined,
  integrationUpdateStatus: undefined,
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.ClearIntegrationContext:
      return {
        ...state,
        integrationUpdateStatus: undefined,
      }
    case ActionType.CreateIntegrationBegin:
      return {
        ...state,
        integrationUpdateStatus: ActionStatus.Pending,
      }
    case ActionType.CreateIntegrationSuccess:
      return {
        ...state,
        integrationUpdateStatus: ActionStatus.Succeeded,
      }
    case ActionType.CreateIntegrationFailure:
      return {
        ...state,
        integrationUpdateStatus: ActionStatus.Failed,
      }
    case ActionType.ListIntegrations:
      return {
        ...state,
        integrationListStatus: ActionStatus.Pending,
      }
    case ActionType.ListIntegrationsSuccess:
      return {
        ...state,
        integrations: action.integrations,
        integrationListStatus: ActionStatus.Succeeded,
      }
    case ActionType.ListIntegrationsFailure:
      return {
        ...state,
        integrations: [],
        integrationListStatus: ActionStatus.Failed,
      }
    default:
      return state
  }
}
